/**
 * How a colleague's or eve's change arrives on the chart, with gtm's cues
 * and timings: its changed words glowing, a chip naming who made it, a
 * removed step fading out as a dashed ghost, a rerouted link's end
 * travelling, a moved step gliding, a new step entering, and the camera
 * following eve when she works for this person. Which steps stay marked as
 * changed is `../workflowChanges`'s, not a cue.
 *
 * Cues come from what changed on screen, not from what the version holds:
 * `changeOf` diffs the chart as shown before and after the doc applied
 * someone else's versions, so a field the person's unsaved edit kept does not
 * glow. It gets a "Kept your edit" chip instead, for as long as the edit it
 * kept is still unsaved there. The chart's own saves give no cue; a save
 * from the person's other tab is named "Your other tab".
 *
 * The cues on screen are one list of timed items (`CueState`): `planCues`
 * turns one change into items, `addCues`, `touchCues` and `expireCues` keep
 * the list, and `useRevisionCues` runs the one timer for the next expiry.
 * Under reduced motion every timed cue holds a static form for
 * {@link STATIC_HOLD_MS} or until the step is touched, and links, steps and
 * the camera jump. Each named change is also said once in the chart's live
 * region, a burst after it as one summary.
 *
 * @module components/workflows/canvas/revisionCues
 */
import type { WorkflowEdge, WorkflowGraph, WorkflowNode } from "@amoreg/contracts";
import { changedSteps, diffWorkflowContent, type StepField, touchedSteps, type WorkflowContentDiff } from "@repo/shared/workflowDiff";
import { graphForSave } from "@repo/shared/workflowEdit";
import { stepFieldNoun } from "@repo/shared/changeWords";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { isRedo, type AppliedVersion, type WorkflowDocState } from "../../../state/workflowDoc";

export const GLOW_MS = 1_800;
export const GHOST_MS = 400;
export const MOVE_MS = 400;
export const ENTER_MS = 200;
export const STATIC_HOLD_MS = 6_000;
export const AGENT_COLOR = "#6f52f5";
/** A burst's changes after the first are said together once none has come for this long. */
export const ANNOUNCE_QUIET_MS = 2_000;
/** However long a burst goes on, it is summed up at least this often. */
export const ANNOUNCE_HOLD_MS = 10_000;
const NOBODY_COLOR = "var(--muted-foreground)";
const OTHER_TAB = "Your other tab";
const YOUR_UNDO = "Your undo";
const YOUR_REDO = "Your redo";

export type CueMotion = "animate" | "static";

export interface CueActor {
  readonly key: string;
  /** The chip's words. Null when nobody can be named: a version taken whole, or one from before revisions were kept. */
  readonly label: string | null;
  /** The chip's title: who it is, and for whom eve acted. */
  readonly title: string | null;
  /** How the live region names them. */
  readonly spoken: string | null;
  readonly color: string;
  readonly forMe: boolean;
  readonly group: string | null;
}

export function cueActor(version: Pick<AppliedVersion, "actor" | "group" | "reverts">, you: string | null): CueActor {
  const { actor, group } = version;
  if (actor === null || actor.via === "backfill") return { key: "nobody", label: null, title: null, spoken: null, color: NOBODY_COLOR, forMe: false, group };
  const key = `${actor.via}:${actor.person}`;
  const yours = you !== null && actor.person === you;
  if (actor.via === "agent") {
    return { key, label: "eve", title: `eve, for ${actor.name}`, spoken: `eve, for ${actor.name},`, color: AGENT_COLOR, forMe: yours, group };
  }
  if (yours && version.reverts !== null) {
    const [suffix, label] = isRedo(version) ? ["redo", YOUR_REDO] : ["undo", YOUR_UNDO];
    return { key: `${key}:${suffix}`, label, title: label, spoken: label, color: actor.color, forMe: true, group };
  }
  if (yours) return { key, label: OTHER_TAB, title: OTHER_TAB, spoken: OTHER_TAB, color: actor.color, forMe: false, group };
  return { key, label: actor.name, title: actor.name, spoken: actor.name, color: actor.color, forMe: false, group };
}

/** Something of someone's the merge kept off the screen, because the person's unsaved edit was there. */
export type KeptFacet =
  | { readonly kind: "field"; readonly field: StepField }
  | { readonly kind: "removed" }
  | { readonly kind: "link"; readonly link: string; readonly target: string };

export interface Kept {
  readonly facets: ReadonlyArray<KeptFacet>;
  readonly by: CueActor;
}

/** Someone else's versions, as they changed the chart on screen. */
export interface ScreenChange {
  readonly before: WorkflowGraph;
  readonly after: WorkflowGraph;
  readonly diff: WorkflowContentDiff<WorkflowGraph>;
  /** Who last changed, added or removed each step whose content or links changed on screen, by what reached the screen. */
  readonly by: ReadonlyMap<string, CueActor>;
  /** Every version's actor, oldest first. */
  readonly actors: ReadonlyArray<CueActor>;
  readonly newest: CueActor;
  readonly kept: ReadonlyMap<string, Kept>;
}

export type DocChange =
  | { readonly kind: "screen"; readonly change: ScreenChange }
  | { readonly kind: "edited" };

type Facet = KeptFacet | { readonly kind: "added" };

/** What one step of the doc did to the screen, or null when it left it alone or only applied the chart's own saves. */
export function changeOf(before: WorkflowDocState, after: WorkflowDocState): DocChange | null {
  const foreign = after.applied.filter((applied) => applied.version > before.confirmed.version && !applied.mine);
  if (foreign.length === 0) return before.local === after.local || after.confirmed.version !== before.confirmed.version ? null : { kind: "edited" };
  const shown = graphForSave(after.local.graph);
  const actors = foreign.map((version) => cueActor(version, after.you));
  const wanted = new Map<string, { readonly step: string; readonly facet: Facet; readonly value: string | null; readonly order: number }>();
  foreign.forEach((version, order) => {
    const theirs = diffWorkflowContent(version.before, version.after);
    const want = (key: string, step: string, facet: Facet, value: string | null) => wanted.set(key, { step, facet, value, order });
    const saved = graphForSave(version.after.graph);
    for (const node of theirs.added) want(`${node.id}\u0000step`, node.id, { kind: "added" }, null);
    for (const node of theirs.removed) want(`${node.id}\u0000step`, node.id, { kind: "removed" }, null);
    for (const { id, fields } of theirs.changed) {
      const node = saved.nodes.find((each) => each.id === id);
      for (const { field } of fields) want(`${id}\u0000${field}`, id, { kind: "field", field }, fieldValue(node, field));
    }
    const links = linkChanges(theirs);
    for (const link of links.removed) want(`link\u0000${link.id}`, link.source, { kind: "link", link: link.id, target: link.target }, null);
    for (const link of links.added) want(`link\u0000${link.id}`, link.source, { kind: "link", link: link.id, target: link.target }, linkValue(link));
  });

  const reached = new Map<string, number>();
  const keptOff = new Map<string, { readonly facets: KeptFacet[]; order: number }>();
  for (const want of wanted.values()) {
    const facet = want.facet;
    const node = shown.nodes.find((each) => each.id === want.step);
    if (!node) {
      if (facet.kind === "removed") reached.set(want.step, Math.max(reached.get(want.step) ?? -1, want.order));
      continue;
    }
    const onScreen =
      facet.kind === "added" ||
      (facet.kind === "field" && fieldValue(node, facet.field) === want.value) ||
      (facet.kind === "link" && linkValue(shown.edges.find((edge) => edge.id === facet.link)) === want.value);
    if (onScreen) {
      reached.set(want.step, Math.max(reached.get(want.step) ?? -1, want.order));
      continue;
    }
    const entry = keptOff.get(want.step) ?? { facets: [], order: want.order };
    entry.facets.push(facet);
    entry.order = Math.max(entry.order, want.order);
    keptOff.set(want.step, entry);
  }
  const actorAt = (order: number) => actors[order] as CueActor;
  return {
    kind: "screen",
    change: {
      before: before.local.graph,
      after: shown,
      diff: diffWorkflowContent(before.local, after.local),
      by: new Map([...reached].map(([step, order]) => [step, actorAt(order)])),
      actors,
      newest: actorAt(actors.length - 1),
      kept: new Map([...keptOff].map(([step, entry]) => [step, { facets: entry.facets, by: actorAt(entry.order) }])),
    },
  };
}

const fieldValue = (node: WorkflowNode | undefined, field: StepField): string | null => {
  if (!node) return null;
  if (field === "type") return node.type;
  if (field === "label") return node.label;
  const value = (node.config as Readonly<Record<string, unknown>>)[field.slice("config.".length)];
  return value === undefined ? null : JSON.stringify(value);
};

const linkValue = (link: WorkflowEdge | undefined): string | null =>
  link ? JSON.stringify([link.source, link.target, link.sourceHandle ?? null]) : null;

function linkChanges(diff: WorkflowContentDiff<WorkflowGraph>) {
  return {
    removed: [...diff.links.removed, ...diff.links.rerouted.map((pair) => pair.before)],
    added: [...diff.links.added, ...diff.links.rerouted.map((pair) => pair.after)],
  };
}


export type Cue =
  | { readonly kind: "glow"; readonly step: string; readonly before: WorkflowNode; readonly color: string; readonly key: number; readonly until: number }
  /** Until the person touches the step, or the actor changes something else. */
  | {
      readonly kind: "chip";
      readonly step: string;
      readonly actor: string;
      readonly label: string;
      readonly title: string;
      readonly color: string;
      readonly at: number;
      readonly until: null;
    }
  /** Goes with its actor's chips and its step's chip, and once the edit it kept is no longer unsaved there. */
  | { readonly kind: "kept"; readonly step: string; readonly kept: Kept; readonly title: string; readonly until: null }
  | { readonly kind: "ghost"; readonly node: WorkflowNode; readonly key: number; readonly until: number }
  | { readonly kind: "travel"; readonly link: string; readonly from: string; readonly until: number }
  | { readonly kind: "move"; readonly step: string; readonly until: number }
  | { readonly kind: "enter"; readonly step: string; readonly until: number };

/** The camera follows one group of eve's changes until the person moves the view. */
export interface Camera {
  readonly group: string | null;
  readonly viewMovesAtStart: number;
}

/** One change as the live region says it, and who made it. */
export interface Announcement {
  readonly who: ReadonlyArray<string>;
  readonly text: string;
}

export interface CuePlan {
  readonly at: number;
  readonly cues: ReadonlyArray<Cue>;
  readonly chipsMoving: ReadonlyArray<string>;
  /** Steps the change removed: whatever still marks them goes. */
  readonly removed: ReadonlyArray<string>;
  readonly camera: Camera | null;
  /** Steps to bring into view, or null to leave the view alone. */
  readonly frame: ReadonlyArray<string> | null;
  readonly announce: Announcement | null;
}

export interface CueContext {
  readonly motion: CueMotion;
  readonly now: number;
  readonly key: number;
  readonly camera: Camera | null;
  readonly viewMoves: number;
}

/** One change's cues. Pure: the same change gives the same plan on every chart. */
export function planCues(change: ScreenChange, context: CueContext): CuePlan {
  const { diff, by, newest } = change;
  const { motion, now, key } = context;
  const hold = (ms: number) => now + (motion === "animate" ? ms : STATIC_HOLD_MS);
  const changed = changedSteps(diff);
  const alive = change.after.nodes.flatMap((node) => (changed.has(node.id) ? [node.id] : []));
  const actorOf = (step: string) => by.get(step) ?? newest;
  const cues: Cue[] = [];
  for (const step of alive) {
    const actor = actorOf(step);
    if (actor.label !== null) {
      cues.push({ kind: "chip", step, actor: actor.key, label: actor.label, title: actor.title ?? actor.label, color: actor.color, at: now, until: null });
    }
  }
  const beforeNodes = new Map(change.before.nodes.map((node) => [node.id, node]));
  for (const { id } of diff.changed) {
    const before = beforeNodes.get(id);
    if (before) cues.push({ kind: "glow", step: id, before, color: actorOf(id).color, key, until: hold(GLOW_MS) });
  }
  for (const node of diff.removed) cues.push({ kind: "ghost", node, key, until: hold(GHOST_MS) });
  if (motion === "animate") {
    for (const { before, after } of diff.links.rerouted) cues.push({ kind: "travel", link: after.id, from: before.id, until: now + MOVE_MS });
    for (const { id } of diff.moved) cues.push({ kind: "move", step: id, until: now + MOVE_MS });
    for (const { id } of diff.added) cues.push({ kind: "enter", step: id, until: now + ENTER_MS });
  }
  for (const [step, kept] of change.kept) cues.push({ kind: "kept", step, kept, title: keptTitle(kept, change.after), until: null });
  const plan = {
    at: now,
    cues,
    chipsMoving: [...new Set(change.actors.flatMap((actor) => (actor.label === null ? [] : [actor.key])))],
    removed: diff.removed.map((node) => node.id),
    announce: announcementOf(change),
  };

  const followed = alive.filter((step) => actorOf(step).forMe);
  const lead = followed[0] === undefined ? null : actorOf(followed[0]);
  if (lead === null) return { ...plan, camera: context.camera, frame: null };
  const camera = context.camera?.group === lead.group ? context.camera : { group: lead.group, viewMovesAtStart: context.viewMoves };
  return { ...plan, camera, frame: camera.viewMovesAtStart === context.viewMoves ? followed : null };
}

const listed = (items: ReadonlyArray<string>) => (items.length < 2 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`);

function keptTitle(kept: Kept, graph: WorkflowGraph): string {
  const who = kept.by.label ?? "A colleague";
  const parts: string[] = [];
  const fields = kept.facets.flatMap((facet) => (facet.kind === "field" ? [stepFieldNoun(facet.field)] : []));
  if (fields.length > 0) parts.push(`its ${[...new Set(fields)].join(", ")}`);
  const targets = kept.facets.flatMap((facet) => (facet.kind === "link" ? [`“${graph.nodes.find((node) => node.id === facet.target)?.label ?? "a removed step"}”`] : []));
  if (targets.length > 0) parts.push(`its link to ${[...new Set(targets)].join(", ")}`);
  if (kept.facets.some((facet) => facet.kind === "removed")) return `Kept this step and your edits to it. ${who} removed it.`;
  return `Kept your edit to ${parts.join(" and ")}. ${who} changed it too.`;
}

function announcementOf(change: ScreenChange): Announcement | null {
  const { diff } = change;
  const labels = new Map([...change.before.nodes, ...change.after.nodes].map((node) => [node.id, node.label]));
  const steps = (ids: ReadonlyArray<string>) => (ids.length > 2 ? `${ids.length} steps` : listed(ids.map((id) => labels.get(id) ?? "a step")));
  const added = new Set(diff.added.map((node) => node.id));
  const removed = new Set(diff.removed.map((node) => node.id));
  const edited = new Set(diff.changed.map((each) => each.id));
  const byActor = new Map<string, { readonly spoken: string; readonly steps: string[] }>();
  for (const [step, actor] of change.by) {
    if (actor.spoken === null) continue;
    const entry = byActor.get(actor.key) ?? { spoken: actor.spoken, steps: [] };
    entry.steps.push(step);
    byActor.set(actor.key, entry);
  }
  const sentences = [...byActor.values()].map(({ spoken, steps: touched }) => {
    const clause = (verb: string, ids: ReadonlyArray<string>) => (ids.length === 0 ? [] : [`${verb} ${steps(ids)}`]);
    const parts = [
      ...clause("added", touched.filter((id) => added.has(id))),
      ...clause("changed", touched.filter((id) => edited.has(id))),
      ...clause("removed", touched.filter((id) => removed.has(id))),
    ];
    return `${spoken} ${parts.length > 0 ? listed(parts) : `changed the links of ${steps(touched)}`}`;
  });
  const keptBy = [...new Set([...change.kept.values()].map((kept) => kept.by.spoken ?? "A colleague"))];
  if (keptBy.length > 0) sentences.push(`Kept your edit to ${steps([...change.kept.keys()])}. ${listed(keptBy)} changed it too`);
  if (sentences.length === 0) return null;
  return { who: [...new Set([...[...byActor.values()].map((entry) => entry.spoken), ...keptBy])], text: sentences.join(". ") };
}

/** What the chart's live region says: one change at once, the rest of a burst summed up when it goes quiet. */
export interface Announcer {
  /** A new key is said again, even in the same words. */
  readonly said: { readonly key: number; readonly text: string } | null;
  /** Nothing new is said before this. */
  readonly quietUntil: number;
  readonly held: ReadonlyArray<Announcement>;
  readonly heldSince: number | null;
}

const QUIET: Announcer = { said: null, quietUntil: 0, held: [], heldSince: null };

function announce(announcer: Announcer, announcement: Announcement | null, at: number, key: number): Announcer {
  if (announcement === null) return announcer;
  if (announcer.held.length === 0 && at >= announcer.quietUntil) {
    return { said: { key, text: announcement.text }, quietUntil: at + ANNOUNCE_QUIET_MS, held: [], heldSince: null };
  }
  const heldSince = announcer.heldSince ?? at;
  const quietUntil = Math.min(Math.max(announcer.quietUntil, at + ANNOUNCE_QUIET_MS), heldSince + ANNOUNCE_HOLD_MS);
  return { ...announcer, held: [...announcer.held, announcement], heldSince, quietUntil };
}

function sumUp(announcer: Announcer, now: number, key: number): Announcer {
  const { held } = announcer;
  if (held.length === 0 || now < announcer.quietUntil) return announcer;
  const text = held.length === 1 ? held[0]!.text : `${listed([...new Set(held.flatMap((each) => each.who))])} made ${held.length} more changes`;
  return { said: { key, text }, quietUntil: now + ANNOUNCE_QUIET_MS, held: [], heldSince: null };
}

export interface CueState {
  readonly cues: ReadonlyArray<Cue>;
  readonly camera: Camera | null;
  /** The steps the newest change that asked for it wants brought into view. */
  readonly frame: { readonly key: number; readonly steps: ReadonlyArray<string> } | null;
  readonly nextKey: number;
  readonly announcer: Announcer;
}

export const NO_CUES: CueState = { cues: [], camera: null, frame: null, nextKey: 1, announcer: QUIET };

const slotOf = (cue: Cue): string => {
  switch (cue.kind) {
    case "ghost":
      return `ghost:${cue.node.id}:${cue.key}`;
    case "travel":
      return `travel:${cue.link}`;
    default:
      return `${cue.kind}:${cue.step}`;
  }
};

const movesOn = (cue: Cue, moving: ReadonlySet<string>) =>
  (cue.kind === "chip" && moving.has(cue.actor)) || (cue.kind === "kept" && moving.has(cue.kept.by.key));

export function addCues(state: CueState, plan: CuePlan): CueState {
  const moving = new Set(plan.chipsMoving);
  const removed = new Set(plan.removed);
  const replaced = new Set(plan.cues.map(slotOf));
  const left = state.cues.filter((cue) => !replaced.has(slotOf(cue)) && !movesOn(cue, moving) && !("step" in cue && removed.has(cue.step)));
  const stays = new Set(left);
  const chipless = new Set(state.cues.flatMap((cue) => (cue.kind === "chip" && !stays.has(cue) && !replaced.has(slotOf(cue)) ? [cue.step] : [])));
  return {
    cues: [...left.filter((cue) => !(cue.kind === "kept" && chipless.has(cue.step))), ...plan.cues],
    camera: plan.camera,
    frame: plan.frame ? { key: state.nextKey, steps: plan.frame } : state.frame,
    nextKey: state.nextKey + 1,
    announcer: announce(state.announcer, plan.announce, plan.at, state.nextKey),
  };
}

const TOUCHABLE: ReadonlySet<Cue["kind"]> = new Set(["glow", "chip", "kept"]);

export function touchCues(state: CueState, steps: ReadonlyArray<string>): CueState {
  if (steps.length === 0) return state;
  const touched = new Set(steps);
  const cues = state.cues.filter((cue) => !(TOUCHABLE.has(cue.kind) && "step" in cue && touched.has(cue.step)));
  return cues.length === state.cues.length ? state : { ...state, cues };
}

export function expireCues(state: CueState, now: number): CueState {
  const cues = state.cues.filter((cue) => cue.until === null || cue.until > now);
  const announcer = sumUp(state.announcer, now, state.nextKey);
  if (announcer !== state.announcer) return { ...state, cues, announcer, nextKey: state.nextKey + 1 };
  return cues.length === state.cues.length ? state : { ...state, cues };
}

/** When the next timed cue ends or a held burst is summed up, or null when nothing is timed. */
export function nextExpiry(state: CueState): number | null {
  let next: number | null = state.announcer.held.length > 0 ? state.announcer.quietUntil : null;
  for (const cue of state.cues) if (cue.until !== null && (next === null || cue.until < next)) next = cue.until;
  return next;
}

function stillKept(facet: KeptFacet, step: string, shown: WorkflowGraph, confirmed: WorkflowGraph): boolean {
  const mine = shown.nodes.find((node) => node.id === step);
  const theirs = confirmed.nodes.find((node) => node.id === step);
  switch (facet.kind) {
    case "removed":
      return mine !== undefined && theirs === undefined;
    case "field":
      return fieldValue(mine, facet.field) !== fieldValue(theirs, facet.field);
    case "link":
      return linkValue(shown.edges.find((edge) => edge.id === facet.link)) !== linkValue(confirmed.edges.find((edge) => edge.id === facet.link));
  }
}

/** "Kept your edit" stands while the screen still differs from the confirmed version where it kept the edit: a save, an undo or a later change ends it. */
export function settleKept(state: CueState, doc: WorkflowDocState): CueState {
  if (!state.cues.some((cue) => cue.kind === "kept")) return state;
  const shown = graphForSave(doc.local.graph);
  const confirmed = graphForSave(doc.confirmed.content.graph);
  let settled = false;
  const cues = state.cues.flatMap((cue): Cue[] => {
    if (cue.kind !== "kept") return [cue];
    const facets = cue.kept.facets.filter((facet) => stillKept(facet, cue.step, shown, confirmed));
    if (facets.length === cue.kept.facets.length) return [cue];
    settled = true;
    if (facets.length === 0) return [];
    const kept = { ...cue.kept, facets };
    return [{ ...cue, kept, title: keptTitle(kept, shown) }];
  });
  return settled ? { ...state, cues } : state;
}

/** The cues after one step of the doc. */
export function followDoc(state: CueState, before: WorkflowDocState, after: WorkflowDocState, context: Pick<CueContext, "motion" | "now" | "viewMoves">): CueState {
  const change = changeOf(before, after);
  let next = state;
  if (change?.kind === "edited") {
    if (state.cues.some((cue) => TOUCHABLE.has(cue.kind))) next = touchCues(state, touchedSteps(before.local, after.local));
  } else if (change?.kind === "screen") {
    const current = expireCues(state, context.now);
    next = addCues(current, planCues(change.change, { ...context, key: current.nextKey, camera: current.camera }));
  }
  return before.local === after.local && before.confirmed === after.confirmed ? next : settleKept(next, after);
}

export interface StepCue {
  readonly glow: { readonly before: WorkflowNode; readonly color: string; readonly key: number } | null;
  readonly chip: { readonly label: string; readonly title: string; readonly color: string; readonly at: number } | null;
  readonly kept: string | null;
  readonly moving: boolean;
  readonly entering: boolean;
}

export interface CanvasCues {
  readonly motion: CueMotion;
  readonly steps: ReadonlyMap<string, StepCue>;
  readonly ghosts: ReadonlyArray<{ readonly node: WorkflowNode; readonly key: number }>;
  readonly travelling: ReadonlyMap<string, string>;
  readonly frame: CueState["frame"];
  readonly announcement: Announcer["said"];
}

const NO_STEP_CUE: StepCue = { glow: null, chip: null, kept: null, moving: false, entering: false };

export function cueView(state: CueState, motion: CueMotion): CanvasCues {
  const steps = new Map<string, StepCue>();
  const ghosts: Array<{ readonly node: WorkflowNode; readonly key: number }> = [];
  const travelling = new Map<string, string>();
  const at = (step: string) => steps.get(step) ?? NO_STEP_CUE;
  for (const cue of state.cues) {
    switch (cue.kind) {
      case "glow":
        steps.set(cue.step, { ...at(cue.step), glow: { before: cue.before, color: cue.color, key: cue.key } });
        break;
      case "chip":
        steps.set(cue.step, { ...at(cue.step), chip: { label: cue.label, title: cue.title, color: cue.color, at: cue.at } });
        break;
      case "kept":
        steps.set(cue.step, { ...at(cue.step), kept: cue.title });
        break;
      case "move":
        steps.set(cue.step, { ...at(cue.step), moving: true });
        break;
      case "enter":
        steps.set(cue.step, { ...at(cue.step), entering: true });
        break;
      case "ghost":
        ghosts.push({ node: cue.node, key: cue.key });
        break;
      case "travel":
        travelling.set(cue.link, cue.from);
        break;
    }
  }
  return { motion, steps, ghosts, travelling, frame: state.frame, announcement: state.announcer.said };
}

/** Reports each step of the doc, before and after; returns how to stop. */
export type DocSubscribe = (listener: (before: WorkflowDocState, after: WorkflowDocState) => void) => () => void;

export interface RevisionCues {
  readonly shown: CanvasCues;
  /** The person selected, dragged, opened or edited these steps. */
  readonly touch: (steps: ReadonlyArray<string>) => void;
  /** The person panned or zoomed: the camera stops following until eve's next request. */
  readonly viewMoved: () => void;
}

const REDUCED = "(prefers-reduced-motion: reduce)";

const subscribeMotion = (onChange: () => void) => {
  const query = window.matchMedia?.(REDUCED);
  query?.addEventListener("change", onChange);
  return () => query?.removeEventListener("change", onChange);
};
const motionNow = (): CueMotion => (window.matchMedia?.(REDUCED).matches ? "static" : "animate");

/** Plays the doc's changes as cues while `subscribe` is given; without it there are none. */
export function useRevisionCues(subscribe: DocSubscribe | undefined): RevisionCues {
  const motion = useSyncExternalStore(subscribeMotion, motionNow, () => "animate" as const);
  const [state, setState] = useState(NO_CUES);
  const motionRef = useRef(motion);
  motionRef.current = motion;
  const viewMoves = useRef(0);

  useEffect(() => {
    if (!subscribe) return;
    const stop = subscribe((before, after) => {
      const context = { motion: motionRef.current, now: Date.now(), viewMoves: viewMoves.current };
      setState((current) => followDoc(current, before, after, context));
    });
    return () => {
      stop();
      setState((current) => ({ ...NO_CUES, nextKey: current.nextKey }));
    };
  }, [subscribe]);

  const due = nextExpiry(state);
  useEffect(() => {
    if (due === null) return;
    // A timer can wake a moment early: expiring at `due` at least never leaves it waiting on itself.
    const timer = window.setTimeout(() => setState((current) => expireCues(current, Math.max(Date.now(), due))), Math.max(0, due - Date.now()));
    return () => window.clearTimeout(timer);
  }, [due]);

  const touch = useCallback((steps: ReadonlyArray<string>) => setState((current) => touchCues(current, steps)), []);
  const viewMoved = useCallback(() => {
    viewMoves.current += 1;
  }, []);
  const shown = useMemo(() => cueView(state, motion), [state, motion]);
  return useMemo(() => ({ shown, touch, viewMoved }), [shown, touch, viewMoved]);
}
